import React from 'react';
import { NavLink } from 'react-router-dom';
import TrustpilotIcon from './TrustpilotIcon';
import { COMPANY_INFO as STATIC_COMPANY_INFO, SERVICES } from '../data/siteData';
import { useCompanyInfo } from '../lib/cms';

export default function Footer() {
  const { companyInfo: COMPANY_INFO } = useCompanyInfo();
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-[#121212] text-slate-300 border-t border-zinc-800">
      {/* Top Hazard Stripe Accent */}
      <div className="h-3 hazard-stripes w-full" />

      {/* Visitkort Motto Banner */}
      <div className="bg-[#0a0a0a] border-b border-zinc-800 py-3 px-4 text-center">
        <p className="text-xs sm:text-sm font-black tracking-widest text-[#F4AF00] uppercase">
          KVALITET &bull; PÅLIDELIGHED &bull; LOKALT ENGAGEMENT
        </p>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-12 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {/* Brand & Address */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=300,quality=90,format=auto/assets/dde638d5-f83c-4036-8d58-4f922ae20635/image_f313bd29.png"
                alt="CMSNord.dk"
                className="h-10 w-auto object-contain"
              />
            </div>
            <p className="text-zinc-200 text-base sm:text-lg font-medium leading-snug">
              Huller & haver - er noget af det vi laver!
            </p>
            <div className="pt-2 text-xs text-zinc-400 space-y-1 border-l-2 border-[#F4AF00] pl-3">
              <p className="font-bold text-white">{COMPANY_INFO.fullAddress}</p>
              <p>CVR: {COMPANY_INFO.cvr}</p>
            </div>

            {/* Social & Review badges */}
            <div className="pt-3 flex flex-wrap gap-2">
              <a
                href={COMPANY_INFO.trustpilotReviews}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-zinc-900 hover:bg-[#00B67A] text-zinc-300 hover:text-white border border-zinc-700 hover:border-[#00B67A] rounded text-xs font-bold transition-all group"
                title="Læs vores anmeldelser på Trustpilot"
              >
                <TrustpilotIcon className="w-3.5 h-3.5 group-hover:brightness-200 transition-all" />
                <span>Trustpilot</span>
              </a>
              <a
                href={COMPANY_INFO.facebookReviews}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-zinc-900 hover:bg-[#1877F2] text-zinc-300 hover:text-white border border-zinc-700 hover:border-[#1877F2] rounded text-xs font-bold transition-all"
                title="Læs vores anmeldelser på Facebook"
              >
                <i className="fa-brands fa-facebook-f text-[#1877F2]" aria-hidden="true" />
                <span>Facebook Anmeldelser</span>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-4 pb-2 border-b border-zinc-800 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[#F4AF00]" />
              <span>Navigation</span>
            </h4>
            <ul className="space-y-2.5 text-sm">
              <li>
                <NavLink to="/" end className="hover:text-[#F4AF00] transition-colors flex items-center gap-2">
                  <i className="fa-solid fa-chevron-right text-[10px] text-[#F4AF00]" aria-hidden="true" />
                  <span>Forside</span>
                </NavLink>
              </li>
              <li>
                <NavLink to="/ydelser" className="hover:text-[#F4AF00] transition-colors flex items-center gap-2">
                  <i className="fa-solid fa-chevron-right text-[10px] text-[#F4AF00]" aria-hidden="true" />
                  <span>Ydelser</span>
                </NavLink>
              </li>
              <li>
                <NavLink to="/galleri" className="hover:text-[#F4AF00] transition-colors flex items-center gap-2">
                  <i className="fa-solid fa-chevron-right text-[10px] text-[#F4AF00]" aria-hidden="true" />
                  <span>Galleri</span>
                </NavLink>
              </li>
              <li>
                <NavLink to="/galleri/foer-efter" className="hover:text-[#F4AF00] transition-colors flex items-center gap-2 text-zinc-400">
                  <i className="fa-solid fa-chevron-right text-[10px] text-[#F4AF00]" aria-hidden="true" />
                  <span>Før &amp; Efter Galleri</span>
                </NavLink>
              </li>
              <li>
                <NavLink to="/om-cmsnord" className="hover:text-[#F4AF00] transition-colors flex items-center gap-2">
                  <i className="fa-solid fa-chevron-right text-[10px] text-[#F4AF00]" aria-hidden="true" />
                  <span>Om CMSNord.dk</span>
                </NavLink>
              </li>
              <li>
                <NavLink to="/kontakt" className="hover:text-[#F4AF00] transition-colors flex items-center gap-2">
                  <i className="fa-solid fa-chevron-right text-[10px] text-[#F4AF00]" aria-hidden="true" />
                  <span>Kontakt</span>
                </NavLink>
              </li>
            </ul>
          </div>

          {/* Services Links */}
          <div>
            <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-4 pb-2 border-b border-zinc-800 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[#F4AF00]" />
              <span>Vores Ydelser</span>
            </h4>
            <ul className="space-y-2.5 text-sm">
              {SERVICES.map((s) => (
                <li key={s.slug}>
                  <NavLink to={`/ydelser/${s.slug}`} className="hover:text-[#F4AF00] transition-colors flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-[#F4AF00] shrink-0" />
                    <span className="font-semibold text-zinc-200">{s.title}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Details */}
          <div>
            <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-4 pb-2 border-b border-zinc-800 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[#F4AF00]" />
              <span>Kontakt</span>
            </h4>
            <div className="space-y-3.5 text-sm">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded bg-[#F4AF00] text-[#121212] flex items-center justify-center shrink-0 font-bold">
                  <i className="fa-solid fa-phone text-xs" aria-hidden="true" />
                </div>
                <div>
                  <span className="text-[10px] text-zinc-400 block uppercase font-bold">Telefon</span>
                  <a
                    href={`tel:${COMPANY_INFO.phoneRaw}`}
                    className="nowrap-contact text-white hover:text-[#F4AF00] font-bold text-sm transition-colors"
                  >
                    <span>{COMPANY_INFO.phone}</span>
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded bg-[#F4AF00] text-[#121212] flex items-center justify-center shrink-0 font-bold">
                  <i className="fa-solid fa-envelope text-xs" aria-hidden="true" />
                </div>
                <div>
                  <span className="text-[10px] text-zinc-400 block uppercase font-bold">E-mail</span>
                  <a
                    href={`mailto:${COMPANY_INFO.email}`}
                    className="nowrap-contact text-zinc-200 hover:text-[#F4AF00] text-sm transition-colors"
                  >
                    <span>{COMPANY_INFO.email}</span>
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded bg-[#F4AF00] text-[#121212] flex items-center justify-center shrink-0 font-bold mt-0.5">
                  <i className="fa-solid fa-location-dot text-xs" aria-hidden="true" />
                </div>
                <div>
                  <span className="text-[10px] text-zinc-400 block uppercase font-bold">Adresse</span>
                  <p className="text-zinc-200 text-xs font-semibold">
                    {COMPANY_INFO.address}<br />
                    {COMPANY_INFO.zipCity}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="pt-8 border-t border-zinc-900 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-zinc-500">
          <p>© {currentYear} {COMPANY_INFO.name}. Alle rettigheder forbeholdes. CVR: {COMPANY_INFO.cvr}</p>
          <p className="text-zinc-400 font-semibold">Entreprenør & Vognmand i Brønderslev</p>
        </div>
      </div>
    </footer>
  );
}
